'use client';
import { createClient } from '@/lib/supabase/client';
import { getCurrentStoreId } from '@/lib/services/storeContext';
import { DEFAULT_PERMISSIONS, MODULE_SUBMENUS, type TeamRole, type RolePermission } from '@/lib/services/userManagementService';
import { MODULES } from '@/lib/services/userManagementService';

export type { RolePermission };

/** Per-sub-menu permission entry with full CRUD */
export interface SubMenuPermission {
  subMenuKey: string;
  enabled: boolean;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  scope: 'own' | 'company';
}

/** Full permission for a module: module-level CRUD + per-sub-menu config */
export interface ModulePermission {
  module: string;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  scope: 'own' | 'company';
  /** Sub-menu level permissions (only for modules that have sub-menus) */
  subMenus: Record<string, SubMenuPermission>;
}

export type UserPermissionsMap = Record<string, ModulePermission>;

function buildDefaultSubMenus(moduleKey: string, enabled: boolean, scope: 'own' | 'company', canAdd = false, canEdit = false, canDelete = false): Record<string, SubMenuPermission> {
  const subs = MODULE_SUBMENUS[moduleKey];
  if (!subs || subs.length === 0) return {};
  return Object.fromEntries(
    subs.map((s) => [s.key, {
      subMenuKey: s.key,
      enabled,
      canView: enabled,
      canAdd: enabled ? canAdd : false,
      canEdit: enabled ? canEdit : false,
      canDelete: enabled ? canDelete : false,
      scope,
    }])
  );
}

/**
 * Fetch per-user permission overrides with sub-menu granularity.
 */
export async function fetchUserPermissions(
  userId: string,
  role: TeamRole
): Promise<UserPermissionsMap> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) return buildDefaultMap(role);

  const { data, error } = await supabase
    .from('ef_user_permissions')
    .select('module, can_view, can_add, can_edit, can_delete, scope, sub_menu_permissions')
    .eq('store_id', storeId)
    .eq('user_id', userId);

  // Start with role defaults
  const base = buildDefaultMap(role);

  if (error || !data || data.length === 0) return base;

  // Apply user-specific overrides
  for (const row of data) {
    const moduleKey = row.module;
    const scope = (row.scope as 'own' | 'company') ?? 'company';
    const canView = row.can_view;

    // Parse stored sub-menu permissions
    let storedSubMenus: Record<string, SubMenuPermission> = {};
    if (row.sub_menu_permissions) {
      try {
        const parsed = typeof row.sub_menu_permissions === 'string'
          ? JSON.parse(row.sub_menu_permissions)
          : row.sub_menu_permissions;
        storedSubMenus = parsed ?? {};
      } catch {
        storedSubMenus = {};
      }
    }

    // Merge with defaults (ensure all sub-menus exist)
    const defaultSubs = buildDefaultSubMenus(moduleKey, canView, scope, row.can_add, row.can_edit, row.can_delete);
    const mergedSubMenus: Record<string, SubMenuPermission> = { ...defaultSubs };
    for (const [k, v] of Object.entries(storedSubMenus)) {
      mergedSubMenus[k] = v as SubMenuPermission;
    }

    base[moduleKey] = {
      module: moduleKey,
      canView,
      canAdd: row.can_add,
      canEdit: row.can_edit,
      canDelete: row.can_delete,
      scope,
      subMenus: mergedSubMenus,
    };
  }

  return base;
}

function buildDefaultMap(role: TeamRole): UserPermissionsMap {
  const map: UserPermissionsMap = {};
  for (const mod of MODULES) {
    const def = DEFAULT_PERMISSIONS[role]?.[mod.key];
    const canView = def?.canView ?? false;
    const canAdd = def?.canAdd ?? false;
    const canEdit = def?.canEdit ?? false;
    const canDelete = def?.canDelete ?? false;
    const scope = (def?.scope as 'own' | 'company') ?? 'company';
    map[mod.key] = {
      module: mod.key,
      canView,
      canAdd,
      canEdit,
      canDelete,
      scope,
      subMenus: buildDefaultSubMenus(mod.key, canView, scope, canAdd, canEdit, canDelete),
    };
  }
  return map;
}

/**
 * Save per-user permission overrides (upsert all modules).
 */
export async function saveUserPermissions(
  userId: string,
  permissions: UserPermissionsMap
): Promise<void> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) throw new Error('No store found');

  const rows = Object.values(permissions).map((p) => ({
    store_id: storeId,
    user_id: userId,
    module: p.module,
    can_view: p.canView,
    can_add: p.canAdd,
    can_edit: p.canEdit,
    can_delete: p.canDelete,
    scope: p.scope ?? 'company',
    sub_menu_permissions: p.subMenus && Object.keys(p.subMenus).length > 0
      ? JSON.stringify(p.subMenus)
      : null,
    updated_at: new Date().toISOString(),
  }));

  const { error } = await supabase
    .from('ef_user_permissions')
    .upsert(rows, { onConflict: 'store_id,user_id,module' });

  if (error) throw new Error(error.message);
}

/**
 * Check if a user has custom (user-level) permission overrides.
 */
export async function hasUserPermissionOverrides(userId: string): Promise<boolean> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) return false;
  const { count } = await supabase
    .from('ef_user_permissions')
    .select('id', { count: 'exact', head: true })
    .eq('store_id', storeId)
    .eq('user_id', userId);
  return (count ?? 0) > 0;
}

/**
 * Remove all user-level overrides (revert to role defaults).
 */
export async function clearUserPermissions(userId: string): Promise<void> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) return;
  const { error } = await supabase
    .from('ef_user_permissions')
    .delete()
    .eq('store_id', storeId)
    .eq('user_id', userId);
  if (error) throw new Error(error.message);
}

/**
 * Get the scope for a specific sub-menu of a module for a user.
 * Returns null if the sub-menu is not enabled.
 */
export function getSubMenuScope(
  permissions: UserPermissionsMap,
  moduleKey: string,
  subMenuKey: string
): 'own' | 'company' | null {
  const mod = permissions[moduleKey];
  if (!mod || !mod.canView) return null;
  const sub = mod.subMenus[subMenuKey];
  if (!sub || !sub.enabled) return null;
  return sub.scope;
}

/**
 * Check if a sub-menu is accessible for a user.
 */
export function canAccessSubMenu(
  permissions: UserPermissionsMap,
  moduleKey: string,
  subMenuKey: string
): boolean {
  const mod = permissions[moduleKey];
  if (!mod || !mod.canView) return false;
  const subs = MODULE_SUBMENUS[moduleKey];
  // If module has no sub-menus defined, access is granted at module level
  if (!subs || subs.length === 0) return true;
  const sub = mod.subMenus[subMenuKey];
  if (!sub) return false;
  return sub.enabled;
}

/**
 * Check if a user can perform a specific action on a sub-menu.
 */
export function canSubMenuAction(
  permissions: UserPermissionsMap,
  moduleKey: string,
  subMenuKey: string,
  action: 'view' | 'add' | 'edit' | 'delete'
): boolean {
  const mod = permissions[moduleKey];
  if (!mod || !mod.canView) return false;
  const subs = MODULE_SUBMENUS[moduleKey];
  if (!subs || subs.length === 0) {
    // No sub-menus: fall back to module-level action
    switch (action) {
      case 'view': return mod.canView;
      case 'add': return mod.canAdd;
      case 'edit': return mod.canEdit;
      case 'delete': return mod.canDelete;
    }
  }
  const sub = mod.subMenus[subMenuKey];
  if (!sub || !sub.enabled) return false;
  switch (action) {
    case 'view': return sub.canView;
    case 'add': return sub.canAdd;
    case 'edit': return sub.canEdit;
    case 'delete': return sub.canDelete;
  }
}
